import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { Search, X, MapPin, Globe, Building2, Map } from 'lucide-react';
import { facebookMetaOptionsService, GeoSearchResult } from '@/services/facebook-meta-options.service';
import { Input } from '@/components/Input';

type LocationType = 'country' | 'city' | 'region';

export interface SelectedLocation {
  type: LocationType;
  code: string;  // country_code for countries, key for cities/regions
  name: string;
  country_name?: string;
}

interface Props {
  selected: SelectedLocation[];
  onChange: (locs: SelectedLocation[]) => void;
}

const TABS: { key: LocationType; label: string; icon: React.ReactNode }[] = [
  { key: 'country', label: 'Countries', icon: <Globe size={13} /> },
  { key: 'city',    label: 'Cities',    icon: <Building2 size={13} /> },
  { key: 'region',  label: 'Regions',   icon: <Map size={13} /> },
];

function useDebounce<T>(value: T, delay = 400): T {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

export const LocationsPicker: React.FC<Props> = ({ selected, onChange }) => {
  const [tab, setTab] = useState<LocationType>('country');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeoSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const dq = useDebounce(query);
  const abortRef = useRef<AbortController | null>(null);

  const primaryCountry = selected.find(s => s.type === 'country')?.code;

  const search = useCallback(async (q: string, t: LocationType) => {
    if (q.length < 2) { setResults([]); return; }
    abortRef.current?.abort();
    setLoading(true);
    try {
      let data: GeoSearchResult[] = [];
      if (t === 'country') data = await facebookMetaOptionsService.searchCountries(q);
      if (t === 'city')    data = await facebookMetaOptionsService.searchCities(q, primaryCountry);
      if (t === 'region')  data = await facebookMetaOptionsService.searchRegions(q, primaryCountry);
      setResults(data);
    } catch { /* abort */ } finally { setLoading(false); }
  }, [primaryCountry]);

  useEffect(() => { search(dq, tab); }, [dq, tab, search]);

  const add = (r: GeoSearchResult) => {
    const code = tab === 'country' ? (r.country_code ?? r.key) : r.key;
    if (selected.some(s => s.code === code && s.type === tab)) return;
    onChange([...selected, { type: tab, code, name: r.name, country_name: r.country_name }]);
    setQuery('');
    setResults([]);
  };

  const remove = (code: string, type: LocationType) =>
    onChange(selected.filter(s => !(s.code === code && s.type === type)));

  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex border-b border-border">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setTab(t.key); setQuery(''); setResults([]); }}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px',
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary',
            )}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`Search ${tab === 'country' ? 'countries' : tab === 'city' ? 'cities' : 'regions'}…`}
          iconLeft={<Search size={14} />}
        />
        {/* Dropdown */}
        {(results.length > 0 || loading) && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border border-border bg-surface-elevated shadow-elevated max-h-56 overflow-y-auto">
            {loading && <div className="px-3 py-2 text-xs text-text-muted">Searching…</div>}
            {results.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => add(r)}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-surface-alt text-left"
              >
                <MapPin size={12} className="text-text-muted shrink-0" />
                <span className="font-medium text-text-primary">{r.name}</span>
                {r.country_name && <span className="text-text-muted">{r.country_name}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5">
        {selected.map(s => (
          <span
            key={`${s.type}-${s.code}`}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-alt px-2.5 py-1 text-xs font-medium text-text-primary"
          >
            {s.type === 'country' && <Globe size={10} className="text-text-muted" />}
            {s.type === 'city'    && <Building2 size={10} className="text-text-muted" />}
            {s.type === 'region'  && <Map size={10} className="text-text-muted" />}
            {s.name}
            <button type="button" onClick={() => remove(s.code, s.type)} className="text-text-muted hover:text-danger ml-0.5">
              <X size={10} />
            </button>
          </span>
        ))}
        {selected.length === 0 && (
          <p className="text-2xs text-text-muted">No locations selected — defaults to your ad account country.</p>
        )}
      </div>
    </div>
  );
};
